import React from 'react';
import {
  Alert,
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import type { TenantSiteEditorSectionId } from '../../../components/tenantSite/TenantSiteRenderer';
import { PublishValidationIssue, PublishValidationResult } from '../../../sitePublishing/types';
import {
  SITE_EDITOR_SECTION_MAP,
  siteEditorSectionForIssuePath,
} from './siteEditorSections';

export type PublishPanelProps = {
  validation: PublishValidationResult | undefined;
  isPublishing: boolean;
  onRetry: () => void;
  onNavigateToSection?: (sectionId: TenantSiteEditorSectionId) => void;
};

const friendlyIssueMessage = (issue: PublishValidationIssue): string => {
  const invalidString = issue.message.match(/^content\.([\w.]+) must be a string\.$/);
  if (!invalidString) return issue.message.replace(/^content\./, '');
  const field = invalidString[1].split('.').pop()?.replace(/([a-z])([A-Z])/g, '$1 $2') ?? 'field';
  return `${field[0].toUpperCase()}${field.slice(1)} has an invalid saved value. Enter text or clear the field.`;
};

const IssueList: React.FC<{
  title: string;
  issues: PublishValidationIssue[];
  severity: 'error' | 'warning';
  onNavigateToSection?: (sectionId: TenantSiteEditorSectionId) => void;
}> = ({ title, issues, severity, onNavigateToSection }) => issues.length > 0 ? (
  <Box>
    <Typography variant="subtitle1" fontWeight={800} color={severity === 'error' ? 'error.main' : 'warning.main'}>
      {title}
    </Typography>
    <List dense disablePadding sx={{ mt: 0.5 }}>
      {issues.map((issue, index) => {
        const sectionId = siteEditorSectionForIssuePath(issue.path, issue.message);
        const section = sectionId ? SITE_EDITOR_SECTION_MAP[sectionId] : undefined;
        const content = (
          <ListItemText
            primary={friendlyIssueMessage(issue)}
            secondary={section ? `Go to ${section.label}` : undefined}
          />
        );
        return (
          <ListItem key={`${issue.code}-${issue.path ?? ''}-${index}`} disableGutters disablePadding={Boolean(section)}>
            {section && onNavigateToSection ? (
              <ListItemButton onClick={() => onNavigateToSection(section.id)} sx={{ borderRadius: 2 }}>
                {content}
              </ListItemButton>
            ) : content}
          </ListItem>
        );
      })}
    </List>
  </Box>
) : null;

const PublishPanel: React.FC<PublishPanelProps> = ({
  validation,
  isPublishing,
  onRetry,
  onNavigateToSection,
}) => {
  if (!validation) {
    return (
      <Stack spacing={2}>
        <Typography color="text.secondary">
          Saving, checking, and publishing happen together. You only need to review this panel if something needs attention.
        </Typography>
        <Button variant="contained" size="large" onClick={onRetry} disabled={isPublishing}>
          {isPublishing ? 'Publishing…' : 'Publish changes'}
        </Button>
      </Stack>
    );
  }

  const bookingErrors = validation.errors.filter((issue) => issue.code.startsWith('BOOKING_'));
  const siteErrors = validation.errors.filter((issue) => !issue.code.startsWith('BOOKING_'));

  return (
    <Stack spacing={2.5}>
      <Alert severity="warning">
        Fix {validation.errors.length === 1 ? 'this item' : `these ${validation.errors.length} items`}, then publish again.
      </Alert>
      <IssueList title="Website" issues={siteErrors} severity="error" onNavigateToSection={onNavigateToSection} />
      <IssueList title="Booking" issues={bookingErrors} severity="error" onNavigateToSection={onNavigateToSection} />
      <IssueList title="Optional improvements" issues={validation.warnings} severity="warning" onNavigateToSection={onNavigateToSection} />
      <Button variant="contained" size="large" onClick={onRetry} disabled={isPublishing}>
        {isPublishing ? 'Publishing…' : 'Try publishing again'}
      </Button>
    </Stack>
  );
};

export default PublishPanel;
